import { ChangeEvent, Dispatch, SetStateAction, useEffect } from 'react'
import {
  Button,
  Flex,
  HStack,
  Heading,
  IconButton,
  Input,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  SimpleGrid,
  useDisclosure,
} from '@chakra-ui/react'

import { CustomFormScoreClassification } from 'components/tools/CustomForm/types'
import { CustomColorPicker, FormItem } from 'components'
import { Pencil } from 'components/icons'
import { focusElementById } from 'utils/events'
import ScoreIntervalConditionButton from './components/ConditionButton'

type Props = {
  classificationIndex: number
  classification: CustomFormScoreClassification
  setClassifications: Dispatch<SetStateAction<CustomFormScoreClassification[]>>
}

export default function ScoreClassificationIntervalForm({
  classification,
  classificationIndex,
  setClassifications,
}: Props) {
  const { isOpen, onClose, onOpen } = useDisclosure()

  function formatClassificationsValuesByName(
    currentClassifications: CustomFormScoreClassification[],
    value: string,
    name: string
  ) {
    const updatedClassification = currentClassifications.map((currentClassification, index) => {
      if (classificationIndex !== index) return currentClassification
      const updatedClassificationField = { ...currentClassification }
      updatedClassificationField[name] = value
      return updatedClassificationField
    })

    return updatedClassification
  }

  const conditionIsBiggerOrLowerThan =
    classification.condition === 'BIGGER_THAN' || classification.condition === 'LOWER_THAN'
  const singleInputNumberName = classification.condition === 'BIGGER_THAN' ? 'from' : 'to'

  function handleOnChangeInput(event: ChangeEvent<HTMLInputElement>) {
    const { value, name } = event.target
    setClassifications((currentClassifications) => {
      return formatClassificationsValuesByName(currentClassifications, value, name)
    })
  }

  function handleOnChangeClassification(value: string, name: string) {
    setClassifications((currentClassifications) => {
      return formatClassificationsValuesByName(currentClassifications, value, name)
    })
  }

  useEffect(() => {
    focusElementById('name')
  }, [])

  return (
    <>
      <IconButton
        variant="ghost"
        aria-label="Atualizar intervalo da classificação"
        icon={<Pencil />}
        onClick={onOpen}
      />

      <Drawer isOpen={isOpen} onClose={onClose}>
        <DrawerOverlay />

        <DrawerContent>
          <DrawerCloseButton />

          <DrawerHeader>
            <Heading>Intervalo da classificação</Heading>
          </DrawerHeader>

          <DrawerBody>
            <FormItem label="Nome do intervalo">
              <HStack>
                <Input
                  autoFocus
                  id="name"
                  placeholder="Tag do intervalo"
                  value={classification.tag}
                  name="tag"
                  onChange={handleOnChangeInput}
                />

                <CustomColorPicker
                  color={classification.color}
                  setColor={(newColor) => handleOnChangeClassification(newColor, 'color')}
                />
              </HStack>
            </FormItem>

            <FormItem label="Condições">
              <SimpleGrid columns={4} gap={2}>
                <ScoreIntervalConditionButton
                  buttonLabel="Maior/igual que"
                  classification={classification}
                  condition="BIGGER_THAN"
                  onClick={() => handleOnChangeClassification('BIGGER_THAN', 'condition')}
                />
                <ScoreIntervalConditionButton
                  buttonLabel="Menor/igual  que"
                  classification={classification}
                  condition="LOWER_THAN"
                  onClick={() => handleOnChangeClassification('LOWER_THAN', 'condition')}
                />
                <ScoreIntervalConditionButton
                  buttonLabel="Intervalo"
                  classification={classification}
                  condition="INTERVAL"
                  onClick={() => handleOnChangeClassification('INTERVAL', 'condition')}
                />
                <ScoreIntervalConditionButton
                  buttonLabel="Igual a"
                  classification={classification}
                  condition="EQUAL"
                  onClick={() => handleOnChangeClassification('EQUAL', 'condition')}
                />
              </SimpleGrid>
            </FormItem>

            {conditionIsBiggerOrLowerThan && (
              <NumberInput
                min={0}
                value={classification.condition === 'BIGGER_THAN' ? classification.from : classification.to}
                onChange={(value) => handleOnChangeClassification(value, singleInputNumberName)}
                defaultValue={0}
              >
                <NumberInputField placeholder="De" />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            )}

            {classification.condition === 'EQUAL' && (
              <NumberInput
                min={0}
                value={classification.from}
                onChange={(value) => {
                  handleOnChangeClassification(value, 'from')
                  handleOnChangeClassification(value, 'to')
                }}
                defaultValue={0}
              >
                <NumberInputField placeholder="De" />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            )}

            {classification.condition === 'INTERVAL' && (
              <Flex align="center" gap={2} minWidth="100%">
                <SimpleGrid columns={2} gap={2}>
                  <NumberInput
                    min={0}
                    value={classification.from}
                    onChange={(value) => handleOnChangeClassification(value, 'from')}
                    defaultValue={0}
                  >
                    <NumberInputField placeholder="De" />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>

                  <NumberInput
                    min={0}
                    value={classification.to}
                    onChange={(value) => handleOnChangeClassification(value, 'to')}
                    defaultValue={0}
                  >
                    <NumberInputField placeholder="Para" />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </SimpleGrid>
              </Flex>
            )}
          </DrawerBody>

          <DrawerFooter>
            <Button onClick={onClose}>Concluir</Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  )
}
